// WizardPage — layout-shell voor cluster-wizard + risico-register + rapport.
// Haalt clusters + survey + risks op; biedt sidebar + context aan de outlet.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import {
    Navigate,
    Outlet,
    useOutletContext,
    useParams,
} from 'react-router-dom';
import AppLayout from '../components/AppLayout.jsx';
import ClusterSidebar from '../clusters/ClusterSidebar.jsx';
import { useClusters } from '../hooks/useClusters.js';
import { useSurvey, isItemFilled } from '../hooks/useSurvey.js';
import { useProject } from '../hooks/useProject.js';
import { api, endpoints } from '../api/client.js';
import { preferredVariants, variantKey } from '../variants.js';

export default function WizardPage() {
    const { projectId } = useParams();
    const { config, loading: clustersLoading, error: clustersError } = useClusters();
    const { project, loading: projectLoading, error: projectError, reload: reloadProject, setProject } = useProject(projectId);
    const preferred = useMemo(() => preferredVariants(project, config), [project, config]);
    const survey = useSurvey(projectId, config, preferred);

    const [risks, setRisks] = useState([]);
    const [riskLoading, setRiskLoading] = useState(false);

    // Hervalideer op de server en neem het register over. Zonder verbinding
    // blijft het laatst bekende register staan.
    const reloadRisks = useCallback(async () => {
        if (!projectId) { return; }
        setRiskLoading(true);
        try {
            const res = await api.post(endpoints.validate(projectId), {});
            setRisks((res?.risks || []).map(r => ({ ...r, cluster: r.cluster_id })));
        } catch (err) {
            console.warn('[risks] validatie mislukt:', err?.message);
        } finally {
            setRiskLoading(false);
        }
    }, [projectId]);

    useEffect(() => { reloadRisks(); }, [reloadRisks]);

    // "Toevoegen aan risicoregister": de server legt afwijkingen zelf vast bij
    // validatie, dus eerst opslaan en dan hervalideren.
    const flushSurvey = survey.flush;
    const dropVariant = survey.dropVariant;
    const addRisk = useCallback(async () => {
        await flushSurvey();
        await reloadRisks();
    }, [flushSurvey, reloadRisks]);

    // Opties van een cluster beheren (toevoegen, naam, voorkeur, laatste verwijderen).
    const updateVariants = useCallback(async (clusterId, body) => {
        const before = project?.metadata?.variants?.[clusterId]?.count || 1;
        const res = await api.put(endpoints.variants(projectId, clusterId), body);
        if (res?.project) { setProject(res.project); }
        if (res?.variants && res.variants.count < before) { dropVariant(clusterId, before); }
        await reloadRisks();
        return res?.variants;
    }, [projectId, project, setProject, reloadRisks, dropVariant]);

    const title = project?.customer_name
        ? `${project.ref || project.zbs_ref || ''} · ${project.customer_name}`
        : undefined;

    // HOOKS MOETEN BOVEN EARLY-RETURNS — anders "Rendered more hooks than during
    // the previous render". Per cluster: welke required items zijn nog niet
    // ingevuld? Resultaat: { [clusterId]: [{ sectionId, itemId, label }] }
    const missingByCluster = useMemo(() => {
        const out = {};
        const data = survey?.data || {};
        for (const cluster of (config?.clusters || [])) {
            const missing = [];
            for (const section of (cluster.sections || [])) {
                for (const item of (section.items || [])) {
                    if (!item.required) continue;
                    const val = data?.[cluster.id]?.[variantKey(section.id, preferred[cluster.id] || 1)]?.[item.id];
                    if (!isItemFilled(item, val)) {
                        missing.push({ sectionId: section.id, itemId: item.id, label: item.label });
                    }
                }
            }
            out[cluster.id] = missing;
        }
        return out;
    }, [config, survey?.data, preferred]);

    // Alleen bij de eerste keer laden de hele pagina vervangen; een herlaad
    // van het project mag de wizard niet onder de gebruiker wegtrekken.
    if (clustersLoading || (projectLoading && !project)) {
        return (
            <AppLayout title={title}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            </AppLayout>
        );
    }

    if (clustersError) {
        return (
            <AppLayout title={title}>
                <Alert severity="error">
                    Cluster-configuratie kon niet worden geladen: {clustersError.message}
                </Alert>
            </AppLayout>
        );
    }

    if (projectError) {
        return (
            <AppLayout title={title}>
                <Alert severity="error">
                    Project niet gevonden: {projectError.message}
                </Alert>
            </AppLayout>
        );
    }

    if (!config?.clusters?.length) {
        return (
            <AppLayout title={title}>
                <Alert severity="warning">Geen clusters geconfigureerd.</Alert>
            </AppLayout>
        );
    }

    return (
        <AppLayout
            title={title}
            sidebar={
                <ClusterSidebar
                    clusters={config.clusters}
                    progress={survey.progress}
                    risks={risks}
                    missingByCluster={missingByCluster}
                />
            }
        >
            <Outlet context={{
                config,
                project,
                survey,
                risks,
                setRisks,
                reloadRisks,
                reloadProject,
                updateVariants,
                preferred,
                addRisk,
                riskLoading,
                missingByCluster,
            }} />
        </AppLayout>
    );
}

/** Child-routes mogen deze helper gebruiken i.p.v. useOutletContext direct. */
export function useWizardContext() {
    return useOutletContext();
}

/** Index-route — kies eerste niet-complete cluster (of A). */
export function WizardIndex() {
    const { projectId } = useParams();
    const { config, survey } = useWizardContext();
    const first = useMemo(() => {
        const clusters = config?.clusters || [];
        const target = clusters.find(c => {
            const p = survey.progress?.[c.id];
            return !p || !p.complete;
        }) || clusters[0];
        return target?.id;
    }, [config, survey.progress]);

    if (!first) { return null; }
    return <Navigate to={`/projects/${projectId}/wizard/${first}`} replace />;
}
